import type {
  CompanyResearchCandidateSearchResponse,
  CompanyResearchQuickSummaryResponse,
} from "./companyResearch";

export type CompanyResearchSessionForm = {
  companyName: string;
  companyUrl: string;
  location: string;
  representativeName: string;
  industry: string;
};

export type CompanyResearchSession = {
  version: 2;
  form: CompanyResearchSessionForm;
  updatedAt: number;
  candidateSearch?: CompanyResearchCandidateSearchResponse;
  quickSummary?: CompanyResearchQuickSummaryResponse;
};

const COMPANY_RESEARCH_SESSION_KEY = "wizmate.company-research.session.v2";
const LEGACY_COMPANY_RESEARCH_SESSION_KEY = "wizmate.company-research.session.v1";
const COMPANY_RESEARCH_SESSION_TTL_MS = 24 * 60 * 60 * 1000;

const emptyForm: CompanyResearchSessionForm = {
  companyName: "",
  companyUrl: "",
  location: "",
  representativeName: "",
  industry: "",
};

function parseJson(raw: string | null): unknown {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object";
}

function isCompanyResearchSession(value: unknown): value is CompanyResearchSession {
  if (!isObject(value)) return false;
  if (value.version !== 2) return false;
  if (!isObject(value.form)) return false;
  if (!isFiniteNumber(value.updatedAt)) return false;
  return true;
}

export function loadCompanyResearchSession(now = Date.now()): CompanyResearchSession | null {
  localStorage.removeItem(LEGACY_COMPANY_RESEARCH_SESSION_KEY);

  const parsed = parseJson(localStorage.getItem(COMPANY_RESEARCH_SESSION_KEY));
  if (!isCompanyResearchSession(parsed)) {
    localStorage.removeItem(COMPANY_RESEARCH_SESSION_KEY);
    return null;
  }

  if (now - parsed.updatedAt > COMPANY_RESEARCH_SESSION_TTL_MS) {
    localStorage.removeItem(COMPANY_RESEARCH_SESSION_KEY);
    return null;
  }

  return {
    version: 2,
    form: {
      companyName: stringValue(parsed.form.companyName),
      companyUrl: stringValue(parsed.form.companyUrl),
      location: stringValue(parsed.form.location),
      representativeName: stringValue(parsed.form.representativeName),
      industry: stringValue(parsed.form.industry),
    },
    updatedAt: parsed.updatedAt,
    candidateSearch: isObject(parsed.candidateSearch)
      ? (parsed.candidateSearch as CompanyResearchCandidateSearchResponse)
      : undefined,
    quickSummary: isObject(parsed.quickSummary)
      ? (parsed.quickSummary as CompanyResearchQuickSummaryResponse)
      : undefined,
  };
}

export function saveCompanyResearchSession(
  session: Omit<CompanyResearchSession, "version" | "updatedAt">,
  now = Date.now()
): void {
  const next: CompanyResearchSession = {
    version: 2,
    form: {
      ...emptyForm,
      ...session.form,
    },
    updatedAt: now,
    candidateSearch: session.candidateSearch,
    quickSummary: session.quickSummary,
  };
  localStorage.setItem(COMPANY_RESEARCH_SESSION_KEY, JSON.stringify(next));
}

export function clearCompanyResearchSession(): void {
  localStorage.removeItem(COMPANY_RESEARCH_SESSION_KEY);
  localStorage.removeItem(LEGACY_COMPANY_RESEARCH_SESSION_KEY);
}
